import { KIND_KEY, KINDS } from "@/lib/printess/constants";
import { useMemo, useCallback } from "react";

/** Helper: Returns [startIndex, endIndex) of the category block (category + products) */
function getCategoryBlockRange(items, categoryIndex) {
  let endIndex = items.length;
  for (let i = categoryIndex + 1; i < items.length; i++) {
    if (items[i][KIND_KEY] === KINDS.CATEGORY) {
      endIndex = i;
      break;
    }
  }
  return [categoryIndex, endIndex];
}

/** Helper: Moves an entire category block to targetIndex */
function moveCategoryBlock(items, categoryIndex, targetIndex) {
  const [start, end] = getCategoryBlockRange(items, categoryIndex);
  const blockLength = end - start;

  const itemsCopy = [...items];
  const block = itemsCopy.splice(start, blockLength);

  // Adjust targetIndex if block is before target, since removal shifts indexes
  const insertIndex = targetIndex > start ? targetIndex - blockLength : targetIndex;

  itemsCopy.splice(insertIndex, 0, ...block);

  return itemsCopy;
}

/** Helper: For React performance, keep references stable for unchanged items */
function preserveStableReferences(prevItems, newItems) {
  const prevKeys = prevItems.map((i) => i.key);
  return newItems.map((item, idx) => {
    const prevIdx = prevKeys.indexOf(item.key);
    return prevIdx !== -1 && prevIdx === idx ? prevItems[prevIdx] : { ...item };
  });
}

export default function useCategoryBlockMover(item, items, setItems) {
  const categoryIndex = useMemo(
    () => items.findIndex((i) => i.key === item.key),
    [items, item.key]
  );

  const prevCategoryIndex = useMemo(() => {
    for (let i = categoryIndex - 1; i >= 0; i--) {
      if (items[i][KIND_KEY] === KINDS.CATEGORY) return i;
    }
    return -1;
  }, [categoryIndex, items]);

  const nextCategoryIndex = useMemo(() => {
    for (let i = categoryIndex + 1; i < items.length; i++) {
      if (items[i][KIND_KEY] === KINDS.CATEGORY) return i;
    }
    return -1;
  }, [categoryIndex, items]);

  const moveUp = useCallback(() => {
    if (prevCategoryIndex === -1) return false;
    const newItems = moveCategoryBlock(items, categoryIndex, prevCategoryIndex);
    setItems((prev) => preserveStableReferences(prev, newItems));
    return true;
  }, [prevCategoryIndex, items, categoryIndex, setItems]);

  const moveDown = useCallback(() => {
    if (nextCategoryIndex === -1) return false;
    const [_, nextCategoryBlockEnd] = getCategoryBlockRange(
      items,
      nextCategoryIndex,
    );
    const newItems = moveCategoryBlock(
      items,
      categoryIndex,
      nextCategoryBlockEnd,
    );
    setItems((prev) => preserveStableReferences(prev, newItems));
    return true;
  }, [nextCategoryIndex, items, categoryIndex, setItems]);

  return {
    canMoveUp: prevCategoryIndex !== -1,
    canMoveDown: nextCategoryIndex !== -1,
    moveUp,
    moveDown,
  };
}
